/**
 * Remind PA (Renata Silva, approved by Isaiah Cotto, 2026-10-27; docs/provider/REMIND_PA.md).
 *
 * Mirrors backend/src/routes/emergeDaySheetPaReminders.js:
 *   GET  /emerge/day-sheet/:facilityId/patients/:patientId/pa-reminder   the dialog's preview (who it goes to, by role)
 *   POST /emerge/day-sheet/:facilityId/patients/:patientId/pa-reminder   send one (Idempotency-Key; body { note? })
 *
 * The provider is told the ROLE a reminder goes to, never a Patient Advocate's name (the TWL pipeline's disclosure
 * default). The note is shown to the PA in eMerge only; it is never emailed.
 */
import { api } from "../../../services/api";

export const NOTE_MAX = 300;

export type RemindRecipientKind = "CASE_HOLDER" | "FACILITY_PA" | "PA_MANAGERS";
export type RemindLimit = "ALREADY_REMINDED" | "RESIDENT_RECENTLY_REMINDED" | "DAILY_LIMIT";
export type RemindNotAvailable =
  | "STAGE" | "STAGE_UNRECOGNISED" | "BUILDING_CLOSED" | "NO_EDIT" | "NOT_A_PROVIDER_RELATIONSHIP" | "NOT_IN_PIPELINE" | "NOT_READY";
/** Ruling 5 (2026-10-27): the provider may see whether their reminder was answered or expired, and when. Never who, never what. */
export type RemindStatus = "OPEN" | "ANSWERED" | "EXPIRED";

/** What a TWL pipeline row says about reminding (the server's own reading; the POST decides again). */
export interface TwlRemind {
  available: boolean;
  not_available: RemindNotAvailable | null;
  /** The caller's own latest reminder for this resident, if any. */
  last_at: string | null;
  /** Its status, and the status's date: OPEN (open until), ANSWERED (when), EXPIRED (when). Absent on an older server. */
  status?: RemindStatus | null;
  status_at?: string | null;
  /** EXPIRED and the PA managers were really told (an escalation was recorded). */
  escalated?: boolean;
  open: boolean;
  open_until: string | null;
  limit: RemindLimit | null;
  /** When the caller could send one, while a limit applies. */
  next_at: string | null;
}

export interface RemindPreview {
  can_remind: boolean;
  stage?: string;
  stage_label?: string;
  recipient: { kind: RemindRecipientKind; words: string } | null;
  limit: { code: RemindLimit; message: string; retry_at: string | null } | null;
  refusal?: { code: string; message: string };
  /** The caller's own latest reminder for this resident and its status (ruling 5). */
  last?: { at: string; status: RemindStatus | null; status_at: string | null; escalated?: boolean } | null;
  note_max: number;
  expiry_days: number;
}

export interface SentReminder {
  id: string;
  created_at: string;
  expires_at: string;
  recipient: { kind: RemindRecipientKind; words: string };
  note: string | null;
  replayed: boolean;
  status?: RemindStatus;
}

/** One answer of the batch state (the workbench and the ATD view). */
export interface RemindStateItem { facility_id: string; patient_id: string; stage: string | null; stage_label: string | null; remind: TwlRemind }

export interface RemindResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  status?: number;
  retryAfterSeconds?: number;
}

const path = (facilityId: string, patientId: string) =>
  `/emerge/day-sheet/${encodeURIComponent(facilityId)}/patients/${encodeURIComponent(patientId)}/pa-reminder`;

/** One key per attempt: a retry with the same key is the same reminder, never a second one. */
export function newReminderKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `rp-${c.randomUUID()}`;
  return `rp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** At most this many residents per state request (the server's own cap). */
export const STATE_MAX = 100;

export const paReminderApi = {
  /** The `remind` state of some residents, each at their building. Pairs at a building the caller cannot open are left out. */
  state: async (pairs: Array<{ facilityId: string; patientId: string }>): Promise<RemindResult<{ ready: boolean; items: RemindStateItem[] }>> => {
    const items = pairs.slice(0, STATE_MAX).map((p) => `${encodeURIComponent(p.facilityId)}:${encodeURIComponent(p.patientId)}`).join(",");
    return (await api.get(`/emerge/day-sheet/pa-reminder-state?items=${items}`)) as RemindResult<{ ready: boolean; items: RemindStateItem[] }>;
  },
  preview: async (facilityId: string, patientId: string): Promise<RemindResult<RemindPreview>> =>
    (await api.get(path(facilityId, patientId))) as RemindResult<RemindPreview>,
  send: async (facilityId: string, patientId: string, note: string, idempotencyKey: string): Promise<RemindResult<SentReminder>> =>
    (await api.post(path(facilityId, patientId), note.trim() ? { note: note.trim() } : {}, {
      headers: { "Idempotency-Key": idempotencyKey },
    })) as RemindResult<SentReminder>,
};
